import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableLoading,
} from "@/components/ui/table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Route as ReportAvailableRoute } from "@/routes/(inventory)/branch/_layout/reports/fast-moving-items/index";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { MoveRightIcon } from "lucide-react";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { ItemType } from "@/gen/inventory/common_pb";
import { useFastMovingItemsData } from "../-hooks/useFastMovingItemsData";

const Component = () => {
  const searchParams = ReportAvailableRoute.useSearch();
  const navigate = useNavigate();
  
  const { data, isLoading, error, isFetching } = useFastMovingItemsData();

  // TableCell and TableHead wrappers for consistent styling
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={cn(tableCellClass, "py-2 font-medium text-center", className)} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={cn(tableHeadClass, "text-center", className)} {...props} />
  );

  const columns = [
    { key: "trade_name", label: "Trade Name" },
    { key: "type", label: "Type" },
    { key: "sales_30", label: "Last 30 Days" },
    { key: "sales_60", label: "Last 60 Days" },
    { key: "sales_90", label: "Last 90 Days" },
    { key: "avg_30", label: "Avg 30 Days" },
    { key: "avg_60", label: "Avg 60 Days" },
    { key: "avg_90", label: "Avg 90 Days" },
    { key: "days_supply", label: "Days of Supply" },
    { key: "actions", label: "" },
  ];

  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => {
                    navigate({
                      to: "/branch/reports",
                    });
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Reports Overview
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Fast Moving Items Report</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <hr className="my-4" />

        <div className="mt-4 border rounded-lg bg-white overflow-hidden">
          {isLoading ? (
            <TableLoading columns={columns} visibleColumns={visibleColumns} />
          ) : !data?.items || data.items.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">No entries found</div>
            </div>
          ) : (
            <>
              <Table className="text-sm">
                <TableHeader className="group">
                  <TableRow>
                    <StyledTableHead rowSpan={2}>Trade Name</StyledTableHead>
                    <StyledTableHead rowSpan={2}>Type</StyledTableHead>
                    <StyledTableHead colSpan={3}>Total Sales</StyledTableHead>
                    <StyledTableHead colSpan={3}>Average Daily Sales</StyledTableHead>
                    <StyledTableHead rowSpan={2}>Days of Supply Remaining</StyledTableHead>
                    <StyledTableHead rowSpan={2}></StyledTableHead>
                  </TableRow>
                  <TableRow className="hover:bg-inherit">
                    <StyledTableHead>Last 30 Days</StyledTableHead>
                    <StyledTableHead>Last 60 Days</StyledTableHead>
                    <StyledTableHead>Last 90 Days</StyledTableHead>
                    <StyledTableHead>Last 30 Days</StyledTableHead>
                    <StyledTableHead>Last 60 Days</StyledTableHead>
                    <StyledTableHead>Last 90 Days</StyledTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((item, index) => {
                    const isLastRow = index === data.totalCount - 1;
                    return (
                      <TableRow key={index} className="font-medium">
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.tradeName}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.itemType === ItemType.MEDICAL ? "MEDICAL" : "NON MEDICAL"}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.totalSalesLast30Days.toFixed(2)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.totalSalesLast60Days.toFixed(2)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.totalSalesLast90Days.toFixed(2)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.averageSalesLast30Days.toFixed(2)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.averageSalesLast60Days.toFixed(2)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{item.averageSalesLast90Days.toFixed(2)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{Math.ceil(item.daysOfSupplyRemaining)}</StyledTableCell>
                        <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                          <Button
                            variant="link"
                            onClick={() => {
                              navigate({
                                to: "/branch/inventory/item/$type/$itemId",
                                params: {
                                  type: item.itemType === ItemType.MEDICAL ? "MEDICAL" : "NON_MEDICAL",
                                  itemId: item.masterId,
                                },
                                search: searchParams,
                              });
                            }}
                          >
                            <MoveRightIcon className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
            <p>{data?.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page - 1
                    }
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const totalPages = data?.totalCount ? Math.ceil(data.totalCount / searchParams.pageSize) : 1;
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((p, idx) => (
                  p === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={p}
                        active={searchParams.page === p}
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: typeof p === 'number' ? p : searchParams.page
                            }
                          });
                        }}
                      >
                        <span className="text-sm">{p}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page + 1
                    }
                  });
                }}
                disabled={searchParams.page >= (data?.totalCount ? Math.ceil(data.totalCount / searchParams.pageSize) : 1)}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: data?.totalCount ? Math.ceil(data.totalCount / searchParams.pageSize) : 1
                    }
                  });
                }}
                disabled={searchParams.page >= (data?.totalCount ? Math.ceil(data.totalCount / searchParams.pageSize) : 1)}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;